import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    MenuItem,
    Divider,
    Box,
    Avatar,
    IconButton,
    Snackbar,
    Alert,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Stack,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { userAPI } from '../services/api';

const StudentProfile = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [profileData, setProfileData] = useState({
        // Basic Information
        username: '',
        full_name: '',
        email: '',
        phone_number: '',
        
        // Personal Information
        date_of_birth: '',
        gender: '',
        
        // Educational Information
        education_level: '',
        institution_name: '',
        grade_level: '',
        
        // Address Information
        home_address: '',
        city: '',
        state: '',
        postal_code: '',
        
        // Emergency Contact
        emergency_contact_name: '',
        emergency_contact_phone: '',
    });

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const response = await userAPI.getStudentProfile();
            if (response.data.success) {
                const data = response.data.data;
                setProfileData({
                    ...data,
                    date_of_birth: formatDate(data.date_of_birth)
                });
            } else {
                setErrorMessage('Failed to load profile');
                setShowError(true);
            }
        } catch (error) {
            setErrorMessage('Failed to load profile');
            setShowError(true);
        }
    };

    const handleSubmit = async (updatedData) => {
        try {
            const formattedData = {
                ...updatedData,
                date_of_birth: updatedData.date_of_birth ? new Date(updatedData.date_of_birth).toISOString().split('T')[0] : null
            };

            const response = await userAPI.updateStudentProfile(formattedData);
            if (response.data.success) {
                setProfileData(updatedData);
                setIsEditing(false);
                setShowSuccess(true);
            } else {
                setErrorMessage(response.data.message || 'Failed to update profile');
                setShowError(true);
            }
        } catch (error) {
            console.error('Profile update error:', error);
            if (error.response?.data?.errors) {
                const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
                setErrorMessage(errorMessages);
            } else {
                setErrorMessage(error.response?.data?.message || 'Failed to update profile');
            }
            setShowError(true);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
    };

    const ProfileHeader = () => (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar
                sx={{
                    width: 100,
                    height: 100,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    mr: 3,
                }}
            >
                {profileData.full_name?.charAt(0) || 'S'}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" gutterBottom>
                    {profileData.full_name}
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Chip label={profileData.education_level} color="primary" variant="outlined" />
                    <Chip label={profileData.institution_name} color="secondary" variant="outlined" />
                </Stack>
            </Box>
            {!isEditing ? (
                <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                >
                    Edit Profile
                </Button>
            ) : null}
        </Box>
    );

    const ProfileSection = ({ title, children }) => (
        <Card sx={{ mb: 3 }}>
            <CardHeader
                title={title}
                titleTypographyProps={{ variant: 'h6', color: 'primary' }}
            />
            <CardContent>
                {children}
            </CardContent>
        </Card>
    );

    const DisplayProfile = () => (
        <>
            <ProfileSection title="Basic Information">
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Username"
                            value={profileData.username}
                            disabled
                            variant="filled"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Full Name"
                            value={profileData.full_name}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Email"
                            value={profileData.email}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            value={profileData.phone_number}
                            disabled
                        />
                    </Grid>
                </Grid>
            </ProfileSection>

            <ProfileSection title="Personal Information">
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Date of Birth"
                            type="date"
                            value={profileData.date_of_birth}
                            disabled
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Gender"
                            value={profileData.gender}
                            disabled
                        />
                    </Grid>
                </Grid>
            </ProfileSection>

            <ProfileSection title="Educational Information">
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Education Level"
                            value={profileData.education_level}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Institution Name"
                            value={profileData.institution_name}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Grade Level"
                            value={profileData.grade_level}
                            disabled
                        />
                    </Grid>
                </Grid>
            </ProfileSection>

            <ProfileSection title="Address Information">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Home Address"
                            multiline
                            rows={2}
                            value={profileData.home_address}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="City"
                            value={profileData.city}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="State"
                            value={profileData.state}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label="Postal Code"
                            value={profileData.postal_code}
                            disabled
                        />
                    </Grid>
                </Grid>
            </ProfileSection>

            <ProfileSection title="Emergency Contact">
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Emergency Contact Name"
                            value={profileData.emergency_contact_name}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Emergency Contact Phone"
                            value={profileData.emergency_contact_phone}
                            disabled
                        />
                    </Grid>
                </Grid>
            </ProfileSection>
        </>
    );

    const EditProfile = () => {
        const [editData, setEditData] = useState(profileData);

        useEffect(() => {
            setEditData(profileData);
        }, [profileData]);

        const handleChange = (e) => {
            const { name, value } = e.target;
            setEditData(prev => ({
                ...prev,
                [name]: value
            }));
        };

        const handleSave = () => {
            handleSubmit(editData);
        };

        const handleCancel = () => {
            setEditData(profileData);
            setIsEditing(false);
        };

        return (
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        sx={{ mr: 1 }}
                    >
                        Save
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </Box>

                <ProfileSection title="Basic Information">
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Username"
                                name="username"
                                value={editData.username}
                                disabled
                                variant="filled"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Full Name"
                                name="full_name"
                                value={editData.full_name}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={editData.email}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                name="phone_number"
                                value={editData.phone_number}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                </ProfileSection>

                <ProfileSection title="Personal Information">
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Date of Birth"
                                name="date_of_birth"
                                type="date"
                                value={editData.date_of_birth}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                label="Gender"
                                name="gender"
                                value={editData.gender}
                                onChange={handleChange}
                            >
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </ProfileSection>

                <ProfileSection title="Educational Information">
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                label="Education Level"
                                name="education_level"
                                value={editData.education_level}
                                onChange={handleChange}
                            >
                                <MenuItem value="high_school">High School</MenuItem>
                                <MenuItem value="university">University</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Institution Name"
                                name="institution_name"
                                value={editData.institution_name}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Grade Level"
                                name="grade_level"
                                value={editData.grade_level}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                </ProfileSection>

                <ProfileSection title="Address Information">
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Home Address"
                                name="home_address"
                                multiline
                                rows={2}
                                value={editData.home_address}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="City"
                                name="city"
                                value={editData.city}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="State"
                                name="state"
                                value={editData.state}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                fullWidth
                                label="Postal Code"
                                name="postal_code"
                                value={editData.postal_code}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                </ProfileSection>

                <ProfileSection title="Emergency Contact">
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Emergency Contact Name"
                                name="emergency_contact_name"
                                value={editData.emergency_contact_name}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Emergency Contact Phone"
                                name="emergency_contact_phone"
                                value={editData.emergency_contact_phone}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                </ProfileSection>
            </form>
        );
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                <ProfileHeader />
                {isEditing ? <EditProfile /> : <DisplayProfile />}
            </Paper>

            <Snackbar
                open={showSuccess}
                autoHideDuration={6000}
                onClose={() => setShowSuccess(false)}
            >
                <Alert severity="success" onClose={() => setShowSuccess(false)}>
                    Profile updated successfully!
                </Alert>
            </Snackbar>

            <Snackbar
                open={showError}
                autoHideDuration={6000}
                onClose={() => setShowError(false)}
            >
                <Alert severity="error" onClose={() => setShowError(false)}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default StudentProfile; 